// Third Party
import uniqBy from "lodash/uniqBy";

// Types
import type { ISystemSettings } from "@Settings/types/Common/model";
import type { Nullable } from "@Common/interfaces/types";
import type { ISaleIdentifier, ISalesPerformance } from "@DataPages/types/salesOperations/salesPerformance/models/salesPerformance";
import type { IGroupInclusion } from "@DataPages/types/salesOperations/salesPerformance/crossStoreReconciliationUtils.ts";

function getIdentifiers(salesPerformanceData: ISalesPerformance[] | undefined, locationId: string): ISaleIdentifier[] {
	if (!salesPerformanceData?.length) {
		return [];
	}
	const seen = new Set<string>();
	const identifiers = salesPerformanceData
		.filter((sale) => sale?.sourceLocationId === locationId)
		.flatMap(
			(sale) =>
				sale?.identifiers?.map((identifierItem) => ({
					...identifierItem,
					label: !!identifierItem.dealerCodeFS
						? `${identifierItem.oemName} (${identifierItem.dealerCodeFS})`
						: identifierItem.oemName,
				})) || []
		)
		.filter((identifierItem) => {
			if (!identifierItem?.oemName || identifierItem.locationId !== locationId) {
				return false;
			}
			const key = identifierItem.label;
			if (seen.has(key)) {
				return false;
			}
			seen.add(key);
			return true;
		});
	identifiers.sort((a, b) => {
		if (a.oemName === "Pre-Owned") return 1;
		if (b.oemName === "Pre-Owned") return -1;
		return a.oemName.localeCompare(b.oemName);
	});
	return identifiers;
}

function getGroupInclusions(systemSettings: Nullable<ISystemSettings>): IGroupInclusion[] | null {
	if (!systemSettings?.crossStoreEmailDistribution) {
		return null;
	}

	const destinationStores = Array.from(
		new Set(systemSettings?.crossStoreEmailDistribution?.flatMap((dist) => dist.accountMappings.map((m) => m.destinationStore)))
	);

	const uniqueDestinationStores = uniqBy(destinationStores, (store) => store.storeId);

	return uniqueDestinationStores
		.map((store) => ({
			label: store.storeName,
			value: store.storeId,
		}))
		.sort((storeA, storeB) => storeA.label.localeCompare(storeB.label));
}

export { getIdentifiers, getGroupInclusions };
